import { useContext, useCallback, useState, ChangeEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';

import { initiateSiteTrial } from '../../../domains/trials/api/initiateSiteTrial';
import { ModalContext } from '../../../contexts/ModalContext';
import { MODAL_ACTIVATE_TRIAL } from '../constants';
import styles from './TrialActivationModal.module.css';
import { SKU } from '../../../typings';

type TrialBundles = {
  id: string;
  duration: number;
  bundleName: `${SKU}_${SKU}`;
  label: string;
};

const trialBundles: TrialBundles[] = [
  {
    id: 'small-trial',
    duration: 15,
    bundleName: 'SKU-5_SKU-35',
    label: 'Small trial (Observe Scale 40k & Ask Business 5k)',
  },
  {
    id: 'big-trial',
    duration: 15,
    bundleName: 'SKU-48_SKU-41',
    label: 'Big Trial (Observe Scale 270k & Ask Scale 10k)',
  },
];

const TrialActivationModal = () => {
  const { isModalOpen, closeModal, openModalProps } =
    useContext(ModalContext) || {};
  const [selectedBundleId, setSelectedBundleId] = useState(trialBundles[0].id);

  const isOpen = isModalOpen?.(MODAL_ACTIVATE_TRIAL);
  const { modalProps, onSuccess } =
    (openModalProps?.[MODAL_ACTIVATE_TRIAL] as {
      modalProps: Record<string, string>;
      onSuccess: () => void;
    }) || {};

  const handleCloseModal = useCallback(() => {
    closeModal?.(MODAL_ACTIVATE_TRIAL);
  }, [closeModal]);

  const handleEnableTrials = async () => {
    // TODO: This will probably be adapted to pick the SKU's/duration
    // etc. from other ways rather than statically assign them.

    if (
      modalProps === undefined ||
      onSuccess === undefined ||
      closeModal === undefined
    ) {
      return null;
    }

    const duration = 15; //days
    const source = `adm:${
      modalProps?.email ? modalProps?.email : modalProps?.organizationId
    }`;

    const trialBundle = trialBundles.find(
      (bundle) => bundle.id === selectedBundleId
    );

    if (!trialBundle) {
      throw new Error(
        `Unable to initialize trial bundle. Selected trial is not supported`
      );
    }

    const errorMessage = `Unable to initialize trial bundle ${trialBundle.label} for site_id: ${modalProps?.siteId}`;

    // Inititate trial for Observe Scale 40,000
    await initiateSiteTrial(
      {
        entity: {
          type: 'site',
          id: modalProps?.siteId,
        },
        hotjar_account_id: modalProps?.accountId,
        bundle_name: trialBundle.bundleName,
        duration,
        source,
      },
      errorMessage
    );

    onSuccess();
  };

  const onBundleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedBundleId(event.target.value);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen ?? false}
      onClose={handleCloseModal}
    >
      <DialogTitle>Initiate Trial</DialogTitle>
      <DialogContent>
        <p>
          You are about to start a trial for Site{' '}
          {modalProps?.name ? modalProps.name : `ID: ${modalProps?.siteId}`}
        </p>
        <p>Choose trial type</p>
        <div className={styles.trialOptions}>
          {trialBundles.map((bundle) => {
            return (
              <div>
                <input
                  onChange={onBundleChange}
                  type="radio"
                  id={bundle.id}
                  name={bundle.label}
                  value={bundle.id}
                  checked={selectedBundleId === bundle.id}
                />
                <label htmlFor={bundle.id}>{bundle.label}</label>
              </div>
            );
          })}
        </div>
        <p>Duration: 15 days</p>
        <br />
        Please remember that this site's tracking code must be configured before
        continuing
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal}>Cancel</Button>
        <Button onClick={handleEnableTrials}>Confirm</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrialActivationModal;
